//src/pages/learner/AssessmentResultsPage.js
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getAssessmentResults,
  getLearnerLeaderboard,
  checkPreAssessmentExistence,
  getComparisonResults,
  downloadLearnerAssessmentPDF,
} from "../../api/learner/learnerApi";
import useAuth from "../../hooks/useAuth";
import Loader from "../../components/common/Loader/Loader";
import { IoMdArrowRoundBack } from "react-icons/io";
import { FaFileDownload } from "react-icons/fa";
import { FaBackward, FaForward } from "react-icons/fa6";

const AssessmentResultsPage = () => {
  const { projectId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [comparisonResults, setComparisonResults] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [marksPerSection, setMarksPerSection] = useState({});
  const [marksPerQuestion, setMarksPerQuestion] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [learnerStats, setLearnerStats] = useState(null);
  const [email, setEmail] = useState("");
  const [showLeaderboard, setShowLeaderboard] = useState(true); // Default to leaderboard
  const [assessmentType, setAssessmentType] = useState("Post"); // Super toggle: Pre/Post/Compare
  const [preExists, setPreExists] = useState(true);
  const [preQnCode, setPreQnCode] = useState("");
  const [postQnCode, setPostQnCode] = useState("");
  const [showTable, setShowTable] = useState(true);
  const [isDownloadHover, setIsDownloadHover] = useState(false);

  // const handleAccessibleButtonClick = () => {
  //   setShowTable((prev) => !prev);
  // };

  const handleDownloadPDF = (assessmentId) => {
    downloadLearnerAssessmentPDF(assessmentId);
  };

  const roundToDecimal = (num, decimals = 2) => {
    if (Number.isInteger(num)) return num; // No rounding needed for integers
    return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
  };

  const clearState = () => {
    setLeaderboard([]);
    setLearnerStats(null);
    setResults([]);
    setHeaders([]);
    setMarksPerSection({});
    setMarksPerQuestion({});
    setComparisonResults([]);
  };

  const backButtonHandler = () => {
    navigate("/projects");
  };

  const fetchLeaderboard = async () => {
    if (assessmentType === "Compare") return; // No leaderboard for Compare
    try {
      setEmail(user?.email);
      const { data } = await getLearnerLeaderboard(
        projectId,
        email,
        assessmentType
      );
      setLeaderboard(data.top14 || []);
      setLearnerStats(data.learner || null);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    }
  };

  const fetchComparisonResults = async (page = 1) => {
    try {
      const { data } = await getComparisonResults(projectId, page, 15);
      setComparisonResults(data.results || []);
      setMarksPerSection(data.marks_per_section || {});
      setMarksPerQuestion(data.marks_per_question || {});
      setTotalPages(data.totalPages);
      setCurrentPage(data.currentPage);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching comparison results:", error);
      setLoading(false);
    }
  };

  const fetchResults = async (page) => {
    if (assessmentType === "Compare") return; // Skip regular fetch for Compare
    try {
      const { data } = await getAssessmentResults(
        projectId,
        page,
        15,
        assessmentType
      );

      setHeaders(
        data.headers.map(
          (header, index) =>
            `${header} (${data.marks_per_question[index] || 0})`
        )
      ); // Static question headers from backend
      setMarksPerSection(data.marks_per_section || {});
      setResults(data.results || []); // Learner rows from backend
      setTotalPages(data.totalPages);
      setCurrentPage(data.currentPage);
      setPostQnCode(data?.questionCodes?.post);
      setPreQnCode(data?.questionCodes?.pre);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching assessment results:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;

    const fetchData = async () => {
      try {
        //clearState();
        if (assessmentType === "Compare") {
          await fetchComparisonResults(currentPage);
        } else {
          await fetchResults(currentPage);
          await fetchLeaderboard();
        }
      } catch (error) {
        console.error("Error in polling:", error);
      }
    };

    const intervalId = setInterval(fetchData, 5000);
    fetchData();

    return () => clearInterval(intervalId);
  }, [projectId, assessmentType, currentPage]);

  const handleAssessmentTypeChange = (newType) => {
    setAssessmentType(newType);
    setCurrentPage(1);
    clearState(); // Clear previous state to avoid stale data
    setLoading(true);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setLoading(true);
    setCurrentPage(newPage);
    if (assessmentType === "Compare") {
      fetchComparisonResults(newPage);
    } else {
      fetchResults(newPage);
    }
  };

  useEffect(() => {
    const checkPreAssessmentAvailability = async () => {
      try {
        const { data } = await checkPreAssessmentExistence(projectId);
        setPreExists(data.questionsExist); // Enable toggle only if questions exist
      } catch {
        setPreExists(false);
      }
    };
    checkPreAssessmentAvailability();
  }, [projectId]);

  // const calculatePercentiles = (learners) => {
  //   if (!learners || learners.length === 0) return [];

  //   // Sort learners by total score in descending order
  //   const sortedLearners = [...learners].sort((a, b) => b.total - a.total);

  //   // Total number of learners
  //   const totalLearners = sortedLearners.length;

  //   // Assign percentiles
  //   return sortedLearners.map((learner, index) => {
  //     const percentile = ((totalLearners - index - 0.5) / totalLearners) * 100;
  //     return {
  //       ...learner,
  //       percentile: Math.round(percentile * 100) / 100, // Round to 2 decimal places
  //     };
  //   });
  // };

  if (loading) {
    return <Loader message="Retrieving your data..." />;
  }

  const downloadCSV = () => {
    if (!comparisonResults.length) return;

    let headers = [
      "Rank,Percentile,Learner,Subjective Pre | Post,Objective Pre | Post,Conversational Pre | Post,Total Pre | Post,Percentage Pre | Post",
    ];

    headers[0] += comparisonResults[0].scores
      .map((_, index) => `,Q${index + 1} Pre | Post`)
      .join("");

    let rows = comparisonResults.map((entry) => {
      let percentagePre = roundToDecimal(
        (entry.preTotal * 100) /
          (marksPerSection.subjective +
            marksPerSection.objective +
            marksPerSection.conversational)
      );
      let percentagePost = roundToDecimal(
        (entry.postTotal * 100) /
          (marksPerSection.subjective +
            marksPerSection.objective +
            marksPerSection.conversational)
      );

      let row = [
        `${entry.rank},${entry.percentile},${entry.learner},${entry.subtotals.pre.subjective} | ${entry.subtotals.post.subjective},${entry.subtotals.pre.objective} | ${entry.subtotals.post.objective},${entry.subtotals.pre.conversational} | ${entry.subtotals.post.conversational},${entry.preTotal} | ${entry.postTotal},${percentagePre} | ${percentagePost}`,
      ];

      row[0] += entry.scores
        .map((score) => `,${score.preScore} | ${score.postScore}`)
        .join("");

      return row;
    });

    let csvContent = headers.concat(rows).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "table_data.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCSV2 = () => {
    if (!results.length) return;

    let headers = [
      "Rank,Percentile,Participant,Subjective,Objective,Conversational,Total,Percentage",
    ];

    headers[0] += results[0].scores
      .map((_, index) => `,Q${index + 1}`)
      .join("");

    let rows = results.map((learner) => {
      let percentage = roundToDecimal(
        (learner.total * 100) /
          (marksPerSection.subjective +
            marksPerSection.objective +
            marksPerSection.conversational)
      );

      let row = [
        `${learner.rank},${learner.percentile},${learner.learner},${learner.subtotals.subjective},${learner.subtotals.objective},${learner.subtotals.conversational},${learner.total},${percentage}`,
      ];

      row[0] += learner.scores.map((score) => `,${score}`).join("");

      return row;
    });

    let csvContent = headers.concat(rows).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "table_data.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalMarks =
    (marksPerSection.subjective || 0) +
    (marksPerSection.objective || 0) +
    (marksPerSection.conversational || 0);

  return (
    <div className="w-full min-h-screen relative overflow-hidden">

  {/* <div className="absolute top-0 left-0 w-72 h-72 bg-purple-400 opacity-20 blur-3xl rounded-full animate-float"></div>
  <div className="absolute bottom-0 right-0 w-72 h-72 bg-blue-400 opacity-20 blur-3xl rounded-full animate-float2"></div> */}
      <div className="lg:w-8/12 w-11/12 mx-auto flex items-center lg:gap-52 md:gap-28 gap-8 pt-5 ">
  <div className="lg:w-8/12 w-11/12 mx-auto relative flex items-center pt-5">
  

 
  <button
    onClick={backButtonHandler}
    className="flex items-center gap-3 px-4 py-2 rounded-full  bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-md hover:scale-105 hover:shadow-lg transition-all duration-300 z-10"
  >
    <IoMdArrowRoundBack className="text-2xl" />
    <span className="font-semibold hidden md:block">Back</span>
  </button>

  
  <h2 className="absolute left-1/2 -translate-x-1/2 text-center text-2xl md:text-3xl font-bold bg-[#36369f] text-transparent bg-clip-text">
    ASSESSMENT RESULT
  </h2>

</div>
        {/* <h3 className="text-center md:text-xl text-base font-bold">
          Assessment Results
        </h3> */}
      </div>
      <div className="w-11/12 mx-auto flex flex-col justify-items-center pt-12">
        <div className="lg:w-9/12 w-full max-w-7xl mx-auto flex flex-col md:flex-row md:justify-between gap-4">
       
          {preExists && (
            <div className="md:w-[58%] w-full">
              <h4 className="font-bold pl-2 pb-1">Select Result Type :</h4>
              <div className="relative flex w-full md:rounded-xl rounded overflow-hidden bg-gray-200 py-3">
                <div
                  className={`absolute top-1 left-1 h-[calc(100%-8px)] w-[calc(33.33%-3px)] lg:h-[calc(90%-4px)] lg:w-[calc(33.33%-3px)] 
            bg-white shadow-lg shadow-purple-400 md:rounded-xl rounded transition-all duration-300 ease-in-out 
            ${assessmentType === "Post" ? "translate-x-[100%]" : ""} 
            ${assessmentType === "Compare" ? "translate-x-[200%]" : ""}`}
                ></div>

                <button
                  className={`w-full font-normal z-10 whitespace-nowrap lg:text-base md:text-sm text-xs ${
                    assessmentType === "Pre"
                      ? "text-purple-700 font-bold"
                      : "text-black"
                  }`}
                  onClick={() => handleAssessmentTypeChange("Pre")}
                >
                  Pre-Assessment
                </button>

                <button
                  className={`w-full  font-normal z-10 whitespace-nowrap lg:text-base md:text-sm text-xs ${
                    assessmentType === "Post"
                      ? "text-purple-700 font-bold"
                      : "text-black"
                  }`}
                  onClick={() => handleAssessmentTypeChange("Post")}
                >
                  Post-Assessment
                </button>

                <button
                  className={`w-full  font-normal z-10 whitespace-nowrap lg:text-base md:text-sm text-xs ${
                    assessmentType === "Compare"
                      ? "text-purple-700 font-bold"
                      : "text-black"
                  }`}
                  onClick={() => handleAssessmentTypeChange("Compare")}
                >
                  Compare
                </button>
              </div>
            </div>
          )}

          
<div className="flex-1 max-w-xs md:max-w-sm mx-auto">
  <h4 className="font-bold pb-2 text-base">
    Select Result Details Type :
  </h4>

  <div className="relative flex items-center w-full h-11 p-1 rounded-xl bg-gray-100 shadow-inner">


    <div
      className={`absolute top-1 left-1 h-[calc(100%-8px)] w-[calc(50%-4px)] 
      rounded-lg bg-[#36369f] 
      shadow-md transition-transform duration-300 ease-in-out 
      ${!showLeaderboard ? "translate-x-full" : "translate-x-0"}`}
    ></div>

  
    <button
      className={`w-full z-10 py-1.5 rounded-lg text-sm font-semibold transition-colors duration-300 
      ${showLeaderboard ? "text-white" : "text-gray-500 hover:text-indigo-600"}`}
      onClick={() => setShowLeaderboard(true)}
    >
      Leaderboard
    </button>

    
    <button
      className={`w-full z-10 py-1.5 rounded-lg text-sm font-semibold transition-colors duration-300 
      ${!showLeaderboard ? "text-white" : "text-gray-500 hover:text-indigo-600"}`}
      onClick={() => setShowLeaderboard(false)}
    >
      Detailed Report
    </button>

  </div>
</div>
        </div>

        <div className=" mx-auto pt-6 pb-4">
          {assessmentType === "Post" && postQnCode ? (
            <div className="mt-3">
              <p className=" text-left font-bold md:text-base text-sm">
                <span>Assessment Code :</span>{" "}
                <span className="text-[#6C63FF] font-extrabold">
                  {postQnCode}
                </span>
              </p>
            </div>
          ) : assessmentType === "Pre" && preQnCode ? (
            <div className="mt-3">
              <p className="text-left font-bold md:text-base text-sm">
                {" "}
                <span>Assessment Code:</span>{" "}
                <span className="text-[#6C63FF] font-extrabold">
                  {preQnCode}
                </span>
              </p>
            </div>
          ) : assessmentType === "Compare" && preQnCode && postQnCode ? (
            <div className="mt-3 md:flex gap-6">
              <p className="text-left font-bold md:text-base text-sm">
                {" "}
                <span>Pre Assessment Code:</span>{" "}
                <span className="text-[#6C63FF] font-extrabold ">
                  {preQnCode}
                </span>
              </p>
              <p className="text-left font-bold md:text-base text-sm">
                <span>Post Assessment Code :</span>{" "}
                <span className="text-[#6C63FF] font-extrabold">
                  {postQnCode}
                </span>{" "}
              </p>
            </div>
          ) : null}
        </div>

        {showLeaderboard ? (
          assessmentType === "Compare" ? (
            comparisonResults.length > 0 ? (
              <div className="lg:w-7/12 w-full mx-auto">
                {/* <h3 className="text-center text-xl font-bold">
                  Comparison Leaderboard
                </h3> */}

                {showTable ? (
                  <div className="h-[400px] overflow-y-auto">
                    <table className="min-w-full text-center border-collapse">
                      <thead className="text-white md:text-sm text-xs font-medium sticky top-0 z-10">
                        <tr className="bg-[#6C63FF]">
                          <th className="md:py-3 py-1.5 px-4">Rank</th>
                          <th className="md:py-3 py-1.5 px-4">Name</th>
                          <th className="md:py-3 py-1.5 px-4">Post Score</th>
                          <th className="md:py-3 py-1.5 px-4">Pre Score</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white shadow-[inset_0px_4px_10px_rgba(0,0,0,0.1)]">
                        {comparisonResults.map((entry, index) => (
                          <tr
                            key={entry.email}
                            className={`border-b border-b-gray-200 hover:bg-gray-50 md:text-base text-sm`}
                          >
                            <td className="md:py-4 py-2 px-4 font-bold whitespace-nowrap text-gray-900">
                              {entry.rank}
                            </td>
                            <td className="md:py-4 py-2 px-4">
                              {entry.learner}
                            </td>
                            <td className="md:py-4 py-2 px-4">
                              {entry.postTotal}
                            </td>
                            <td className="md:py-4 py-2 px-4">
                              {entry.preTotal}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <></>
                )}

                {totalPages > 1 && (
                  <div className="flex items-center gap-4 py-2 justify-center">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="flex items-center gap-3 bg-[#6C63FF] py-1.5 px-3 rounded-lg md:text-base text-xs"
                    >
                      <FaBackward className="text-white" />
                      <span className="text-white">Prev</span>
                    </button>
                    <span className="md:text-base text-xs">
                      <strong className="bg-gray-400 py-1.5 px-2 rounded-xl text-white">
                        {currentPage}
                      </strong>{" "}
                      of{" "}
                      <strong className="bg-blue-500 py-1.5 px-2 rounded-xl text-white">
                        {totalPages}
                      </strong>
                    </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="flex items-center gap-3 bg-[#6C63FF] py-1.5 px-3 rounded-lg md:text-base text-xs"
                    >
                      <span className="text-white">Next</span>
                      <FaForward className="text-white" />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-xl">No Results Found</div>
            )
          ) : leaderboard.length > 0 ? (
           <div className="lg:w-7/12 w-full mx-auto">
  {showTable ? (
    <div className="h-[400px] max-h-[400px] overflow-y-auto 
    rounded-2xl bg-white/60 backdrop-blur-lg border border-gray-200 shadow-xl mb-6">

      <table className="min-w-full text-center border-collapse">

    
        <thead className="sticky top-0 z-10">
          <tr className="bg-[#36369f] text-white text-sm md:text-base">
            <th className="py-3 px-4">Rank</th>
            <th className="py-3 px-4">Name</th>
            <th className="py-3 px-4">Score</th>
            <th className="py-3 px-4">Performance</th>
          </tr>
        </thead>

        
        <tbody className="bg-white">
          {leaderboard.map((entry, index) => {
            const percentage = Math.round(
              (entry.total / totalMarks) * 100
            );

            let progressColor;

            if (percentage >= 80) {
              progressColor =
                "bg-gradient-to-r from-green-400 to-green-600";
            } else if (percentage >= 55) {
              progressColor =
                "bg-gradient-to-r from-indigo-500 to-purple-600";
            } else if (percentage >= 30) {
              progressColor =
                "bg-gradient-to-r from-yellow-400 to-orange-500";
            } else {
              progressColor =
                "bg-gradient-to-r from-red-400 to-red-600";
            }

            return (
              <tr
                key={entry.email || index}
                className="border-b border-gray-200 hover:bg-indigo-50 transition"
              >
                <td className="py-3 px-4 font-bold text-indigo-600">
                  {entry.rank}
                </td>

                <td className="py-3 px-4 text-gray-800">
                  {entry.learner}
                </td>

                <td className="py-3 px-4 font-semibold">
                  {entry.total}
                </td>

                <td className="py-3 px-4">
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`${progressColor} h-3 rounded-full`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-xs">{percentage}%</span>
                </td>
              </tr>
            );
          })}
        </tbody>

      </table>
    </div>
  ) : null}
</div>
          ) : (
            <div className="text-center text-xl"> No Results Found</div>
          )
        ) : assessmentType === "Compare" ? (
          comparisonResults.length > 0 ? (
            <div className="w-full">
             <button
  onClick={downloadCSV}
  onMouseEnter={() => setIsDownloadHover(true)}
  onMouseLeave={() => setIsDownloadHover(false)}
  className="group relative flex items-center gap-2 px-5 py-2.5  rounded-xl font-semibold text-white 
  bg-[#36369f] shadow-lg shadow-purple-400/30 hover:shadow-xl hover:shadow-purple-500/40 
  hover:scale-105 active:scale-95 transition-all duration-300 overflow-hidden"
>

  
  <span className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition duration-300"></span>


  <span className="text-lg">
    <FaFileDownload className="group-hover:translate-y-1 transition-transform duration-300" />
  </span>

 
  <span className="text-sm md:text-base whitespace-nowrap">
    {isDownloadHover ? "Download Table as CSV" : "Download"}
  </span>

</button>

              {showTable ? (
                <div className="h-[400px] overflow-y-auto">
                  <table className="min-w-full text-center border-collapse">
                    <thead className="bg-gray-100 text-white text-sm font-medium sticky top-0 z-20">
                      <tr className="bg-[#36369f] py-3">
                        <th className="md:py-4 py-2 md:px-8 px-4 text-left sticky left-0 bg-[#36369f] z-10 w-[40px] md:w-[60px]">
                          Rank
                        </th>

                        <th className="md:py-4 py-2 pl-4 md:pr-32 pr-10 text-left sticky bg-[#36369f] z-20 left-[65px] md:left-[98px] w-[60px] md:w-full">
                          Participant
                        </th>
                        <th className="md:py-4 py-2 px-4">Percentile</th>
                        <th className="md:py-4 py-2 px-4">
                          Subjective({marksPerSection.subjective || 0}){" "}
                        </th>
                        <th className="md:py-4 py-2 px-4">
                          Objective({marksPerSection.objective || 0}){" "}
                        </th>
                        <th className="md:py-4 py-2 px-4">
                          Conversational({marksPerSection.conversational || 0}){" "}
                        </th>
                        <th className="md:py-4 py-2 px-6">
                          Total(
                          {marksPerSection.subjective +
                            marksPerSection.objective +
                            marksPerSection.conversational}
                          ){" "}
                        </th>
                        <th className="md:py-4 py-2 px-4">Percentage(%) </th>
                        {comparisonResults[0]?.scores.map((_, index) => (
                          <th key={`header-${index}`} className="py-3 px-8 ">
                            Q{index + 1}({marksPerQuestion?.[index] || 0})
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white shadow-[inset_0px_4px_10px_rgba(0,0,0,0.1)]">
                      {comparisonResults.map((entry, index) => (
                        <tr
                          key={entry.email}
                          className="border-b border-b-gray-200 hover:bg-gray-50 md:text-base text-sm"
                        >
                          <td className="font-bold text-gray-900 text-left md:pl-8 pl-5 sticky left-0 bg-white z-10 w-[40px] md:w-[60px]">
                            {entry.rank}.
                          </td>
                          <td className="font-bold text-left md:py-4 py-2 px-4 sticky bg-white z-10 left-[65px] md:left-[98px] w-[60px] md:w-full">
                            {entry.learner}
                          </td>
                          <td className="font-bold md:py-4 py-2 px-4">
                            {entry.percentile}
                          </td>
                          <td className="md:py-4 py-2 px-4">{`${entry.subtotals.pre.subjective} | ${entry.subtotals.post.subjective}`}</td>
                          <td className="md:py-4 py-2 px-4">{`${entry.subtotals.pre.objective} | ${entry.subtotals.post.objective}`}</td>
                          <td className="md:py-4 py-2 px-4">{`${entry.subtotals.pre.conversational} | ${entry.subtotals.post.conversational}`}</td>
                          <td className="md:py-4 py-2 px-4">{`${entry.preTotal} | ${entry.postTotal}`}</td>
                          <td className="md:py-4 py-2 px-4">
                            {`${
                              roundToDecimal(
                                (entry.preTotal * 100) /
                                  (marksPerSection.subjective +
                                    marksPerSection.objective +
                                    marksPerSection.conversational)
                              ) || "NA"
                            } | ${
                              roundToDecimal(
                                (entry.postTotal * 100) /
                                  (marksPerSection.subjective +
                                    marksPerSection.objective +
                                    marksPerSection.conversational)
                              ) || "NA"
                            }`}
                          </td>
                          {entry.scores.map((score, i) => (
                            <td
                              key={`data-${i}`}
                              className="md:py-4 py-2 px-4"
                            >{`${score.preScore} | ${score.postScore}`}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="w-full mt-4  shadow-blue-300  mb-3 ">
                  <div className="flex  bg-blue-700 py-3 rounded-t-lg ">
                    <div className="mr-auto">
                      <p className="text-white font-bold  text-left mx-2">
                        Rank
                      </p>
                    </div>
                    <div className="w-1/12 mx-4  ">
                      <p className="text-white font-bold text-left">
                        Percentile{" "}
                      </p>
                    </div>
                    <div className="w-2/12 ml-auto  ">
                      <p className="text-white font-bold text-left">
                        Participant
                      </p>
                    </div>

                    <div className=" w-6/12 flex  space-x-3 ml-auto mr-4">
                      <div className="w-1/6 ml-auto  ">
                        <h1 className="text-white text-center">Subjective </h1>
                        <h1 className="text-white text-center">
                          ({marksPerSection.subjective || 0})
                        </h1>
                      </div>
                      <div className="w-1/6 ml-auto  ">
                        <h1 className="text-white text-center">Objective </h1>
                        <h1 className="text-white text-center">
                          ({marksPerSection.objective || 0})
                        </h1>
                      </div>
                      <div className="w-1/6 ml-auto ">
                        <h1 className="text-white text-center">
                          Conversational
                        </h1>
                        <h5 className="text-white text-center">
                          ({marksPerSection.conversational || 0})
                        </h5>
                      </div>
                      <div className="w-1/6 ml-auto">
                        <h1 className="text-white text-center">Total </h1>
                        <h1 className="text-white text-center">
                          (
                          {marksPerSection.subjective +
                            marksPerSection.objective +
                            marksPerSection.conversational}
                          )
                        </h1>
                      </div>
                      <div className="w-1/6  ml-auto">
                        <h1 className="text-white text-center">percentage</h1>
                        <h1 className="text-white text-center">(%)</h1>
                      </div>
                    </div>

                    <div className="w-4/12 space-x-3 overflow-x-scroll  flex ml-auto">
                      {comparisonResults[0]?.scores.map((_, index) => (
                        <div key={index}>
                          <div className="w-11/12 flex text-white mx-5">
                            Q{index + 1}({marksPerQuestion?.[index] || 0})
                          </div>
                          <div></div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {comparisonResults.map((entry, index) => (
                    <div
                      key={entry.email}
                      className="flex py-3  w-full mt-4 border-2 shadow-lg shadow-blue-300  mb-3 rounded-lg "
                    >
                      <div className="mr-auto">
                        <p className="font-bold  text-left mx-2">
                          {entry.rank}.
                        </p>
                      </div>
                      <div className="w-1/12 mx-4  ">
                        <p className=" font-bold ">{`${entry.percentile}`} </p>
                      </div>

                      <div className="w-2/12 ml-4">
                        <p className=" font-bold text-left ">{entry.learner}</p>
                      </div>

                      <div className=" w-6/12 flex  space-x-3 ml-auto mr-4">
                        <div className="w-1/6  ml-auto">
                          <h1 className=" text-center">
                            {" "}
                            {`${entry.subtotals.pre.subjective} | ${entry.subtotals.post.subjective}`}
                          </h1>
                        </div>
                        <div className="w-1/6 ml-auto">
                          <h1 className=" text-center">{`${entry.subtotals.pre.objective} | ${entry.subtotals.post.objective}`}</h1>
                        </div>
                        <div className="w-1/6 ml-auto">
                          <h1 className="text-center">
                            {" "}
                            {`${entry.subtotals.pre.conversational} | ${entry.subtotals.post.conversational}`}
                          </h1>
                        </div>
                        <div className="w-1/6 ml-auto">
                          <h1 className=" text-center">
                            {" "}
                            {`${entry.preTotal} | ${entry.postTotal}`}
                          </h1>
                        </div>
                        <div className="w-1/6 ml-auto">
                          <h1 className=" text-center">
                            {" "}
                            {`${
                              roundToDecimal(
                                (entry.preTotal * 100) /
                                  (marksPerSection.subjective +
                                    marksPerSection.objective +
                                    marksPerSection.conversational)
                              ) || "NA"
                            } | ${
                              roundToDecimal(
                                (entry.postTotal * 100) /
                                  (marksPerSection.subjective +
                                    marksPerSection.objective +
                                    marksPerSection.conversational)
                              ) || "NA"
                            }`}
                          </h1>
                        </div>
                      </div>

                      <div className="w-4/12 space-x-3 overflow-x-scroll  flex ml-auto ">
                        {entry.scores.map((score, i) => (
                          <div key={i}>
                            <div
                              className="w-11/12 flex mx-7"
                              key={i}
                            >{`${score.preScore} | ${score.postScore}`}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex items-center gap-4 md:py-3 py-5 justify-center">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="flex items-center gap-3 bg-[#6C63FF] py-1.5 px-3 rounded-lg md:text-base text-xs"
                  >
                    <FaBackward className="text-white" />
                    <span className="text-white">Prev</span>
                  </button>
                  <span className="md:text-base text-xs">
                    <strong className="bg-gray-400 py-1.5 px-2 rounded-xl text-white">
                      {currentPage}
                    </strong>{" "}
                    of{" "}
                    <strong className="bg-blue-500 py-1.5 px-2 rounded-xl text-white">
                      {" "}
                      {totalPages}
                    </strong>
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="flex items-center gap-3 bg-[#6C63FF] py-1.5 px-3 rounded-lg md:text-base text-xs"
                  >
                    <span className="text-white">Next</span>
                    <FaForward className="text-white" />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-xl">No Results Found</div>
          )
        ) : results?.length > 0 ? (
          <div>
            <button
              className="mb-2 bg-blue-600 text-white font-bold py-2 px-4 rounded shadow hover:bg-blue-700 transition"
              onClick={downloadCSV2}
              onMouseEnter={() => setIsDownloadHover(true)}
              onMouseLeave={() => setIsDownloadHover(false)}
             >
                  {isDownloadHover ? (
                   "Download Table as CSV"
                    ) : (
                   <FaFileDownload className="text-xl" />
                  )}
            </button>
            {showTable ? (
              <div className="h-[400px] overflow-y-auto">
                <table className="min-w-full text-center border-collapse">
                  <thead className="bg-gray-100 text-white text-sm font-medium sticky top-0 z-20">
                    <tr className="bg-[#36369f] py-3">
                      <th className="md:py-4 py-2 md:px-8 px-4 text-left sticky left-0 bg-[#36369f] z-10 w-[40px] md:w-[60px]">
                        Rank
                      </th>
                      <th className="md:py-4 py-2 pl-4 md:pr-32 pr-10 text-left sticky bg-[#36369f] z-20 left-[65px] md:left-[98px] w-[60px] md:w-full">
                        Participant
                      </th>
                      <th className=" md:py-4 py-2 px-5">Percentile</th>
                      <th className=" md:py-4 py-2 px-5">
                        Subjective({marksPerSection.subjective || 0})
                      </th>
                      <th className=" md:py-4 py-2 px-5">
                        Objective({marksPerSection.objective || 0})
                      </th>
                      <th className=" md:py-4 py-2 px-5">
                        Conversational({marksPerSection.conversational || 0})
                      </th>
                      <th className=" md:py-4 py-2 px-5">
                        Total(
                        {marksPerSection.subjective +
                          marksPerSection.objective +
                          marksPerSection.conversational}
                        )
                      </th>
                      <th className=" md:py-4 py-2 px-5">Percentage(%)</th>
                      <th className=" md:py-4 py-2 px-5">Report</th>
                      {headers.map((header) => (
                        <th key={header} className=" md:py-4 py-2 px-6">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white shadow-[inset_0px_4px_10px_rgba(0,0,0,0.1)]">
                    {results.map((learner) => (
                      <tr
                        key={learner.email}
                        className="border-b border-b-gray-200 hover:bg-gray-50 md:text-base text-sm"
                      >
                        <td className="font-bold text-gray-900 text-left md:pl-8 pl-5 sticky left-0 bg-white z-10 w-[40px] md:w-[60px]">
                          {learner.rank}
                        </td>
                        <td className="font-bold text-left md:py-4 py-2 px-4 sticky bg-white z-10 left-[65px] md:left-[98px] w-[60px] md:w-full">
                          {learner.learner}
                        </td>
                        <td className="font-bold md:py-4 py-2 px-4">
                          {learner.percentile}
                        </td>
                        <td className="md:py-4 py-2 px-4">
                          {learner.subtotals.subjective}
                        </td>
                        <td className="md:py-4 py-2 px-4">
                          {learner.subtotals.objective}
                        </td>
                        <td className="md:py-4 py-2 px-4">
                          {learner.subtotals.conversational}
                        </td>
                        <td className="md:py-4 py-2 px-4">{learner.total}</td>
                        <td className="md:py-4 py-2 px-4">
                          {roundToDecimal(
                            (learner.total * 100) /
                              (marksPerSection.subjective +
                                marksPerSection.objective +
                                marksPerSection.conversational)
                          )}
                        </td>
                        <td>
                          <button
                            className="px-4 py-1 md:text-xl text-base"
                            onClick={() => handleDownloadPDF(learner._id)}
                          >
                            <FaFileDownload aria-label="download" />
                          </button>
                        </td>
                        {learner.scores.map((score, index) => (
                          <td
                            key={`score-${index}`}
                            className="md:py-4 py-2 px-4"
                          >
                            {score}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="w-full mt-4  shadow-blue-300  mb-3 ">
                <div className="flex  bg-blue-700 py-3 rounded-t-lg ">
                  <div className="mr-auto">
                    <p className="text-white font-bold  text-left mx-2">Rank</p>
                  </div>
                  <div className="w-1/12 mx-4  ">
                    <p className="text-white font-bold text-left">
                      Percentile{" "}
                    </p>
                  </div>
                  <div className="w-2/12 ml-auto  ">
                    <p className="text-white font-bold text-left">
                      Participant
                    </p>
                  </div>

                  <div className=" w-6/12 flex  space-x-3 ml-auto">
                    <div className="w-1/6 ">
                      <h1 className="text-white text-center">Subjective</h1>
                      <h1 className="text-white text-center">
                        ({marksPerSection.subjective || 0})
                      </h1>
                    </div>
                    <div className="w-1/5 ml-auto">
                      <h1 className="text-white text-center">Objective</h1>
                      <h1 className="text-white text-center">
                        ({marksPerSection.objective || 0})
                      </h1>
                    </div>
                    <div className="w-1/6 ml-auto  ">
                      <h1 className="text-white text-center">Conversational</h1>
                      <h5 className="text-white text-center">
                        ({marksPerSection.conversational || 0})
                      </h5>
                    </div>
                    <div className="w-1/6 ml-auto ">
                      <h1 className="text-white text-center">Total</h1>
                      <h1 className="text-white text-center">
                        (
                        {marksPerSection.subjective +
                          marksPerSection.objective +
                          marksPerSection.conversational}
                        )
                      </h1>
                    </div>
                    <div className="w-1/6  ml-auto">
                      <h1 className="text-white text-center">percentage (%)</h1>
                    </div>
                    <div className="w-1/6 ml-auto">
                      <h1 className="text-white text-center">Report</h1>
                    </div>
                  </div>

                  <div className="w-4/12 space-x-3 overflow-x-scroll  flex ml-auto">
                    {headers.map((header) => (
                      <div>
                        <div
                          className="w-11/12 flex text-white mx-5"
                          key={header}
                        >
                          {header}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {results.map((learner) => (
                  <div
                    key={learner.email}
                    className="flex py-3  w-full mt-4 border-2 shadow-lg shadow-blue-300  mb-3 rounded-lg "
                  >
                    <div className="mr-auto">
                      <p className="font-bold  text-left mx-2">
                        {learner.rank}
                      </p>
                    </div>
                    <div className="w-1/12 mx-4  ">
                      <p className=" font-bold ">{learner.percentile} </p>
                    </div>

                    <div className="w-2/12  ml-4">
                      <p className=" font-bold text-left ">{learner.learner}</p>
                    </div>

                    <div className=" w-6/12 flex  space-x-3 ml-auto">
                      <div className="w-1/5  ">
                        <h1 className=" text-center">
                          {learner.subtotals.subjective}
                        </h1>
                      </div>
                      <div className="w-1/6 ">
                        <h1 className=" text-center">
                          {learner.subtotals.objective}
                        </h1>
                      </div>
                      <div className="w-1/6  ">
                        <h1 className="text-center">
                          {learner.subtotals.conversational}
                        </h1>
                      </div>
                      <div className="w-1/6 ">
                        <h1 className=" text-center">{learner.total}</h1>
                      </div>
                      <div className="w-1/6  ">
                        <h1 className="text-black text-center">
                          {`${roundToDecimal(
                            (learner.total * 100) /
                              (marksPerSection.subjective +
                                marksPerSection.objective +
                                marksPerSection.conversational)
                          )}`}
                        </h1>
                      </div>
                      <div>
                        <button
                          className="bg-gray-500 text-white px-4 py-1 rounded-md mx-2 "
                          onClick={() => handleDownloadPDF(learner._id)}
                        >
                          Download
                        </button>
                      </div>
                    </div>

                    <div className="w-4/12 space-x-3 overflow-x-scroll  flex ml-auto">
                      {learner.scores.map((score, index) => (
                        <div key={index}>
                          <div className="w-11/12 flex mx-6" key={index}>
                            {score}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center gap-4 py-2 justify-center">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex items-center gap-3 bg-[#6C63FF] py-1.5 px-3 rounded-lg md:text-base text-xs"
                >
                  <FaBackward className="text-white" />
                  <span className="text-white">Prev</span>
                </button>
                <span className="md:text-base text-xs">
                  <strong className="bg-gray-400 py-1.5 px-2 rounded-xl text-white">
                    {currentPage}
                  </strong>{" "}
                  of{" "}
                  <strong className="bg-blue-500 py-1.5 px-2 rounded-xl text-white">
                    {" "}
                    {totalPages}
                  </strong>
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-3 bg-[#6C63FF] py-1.5 px-3 rounded-lg md:text-base text-xs"
                >
                  <span className="text-white">Next</span>
                  <FaForward className="text-white" />
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center text-xl">No Results Found</div>
        )}
      </div>
    </div>
  );
};

export default AssessmentResultsPage;
